import { useRef } from 'react';

import { usePermissions } from '@mint-lib/profile-helpers';
import styled, { css } from '@mint-lib/styled';

import { StaticNumericSpan, StaticPercentSpan, Tooltip } from '@myn/mui';

import ConditionalCurrencyField from '../../../../../../atoms/ConditionalCurrencyField.jsx';
import { VERSION_ROW_TYPES } from '../../../../../../constants/versionsColumnsObject.js';
import { useCampaignDataSelector } from '../../../../../../hooks/useGetCampaignData.js';
import { useTableScale } from '../../../../../../hooks/useTableScale.js';
import { useTextOverflow } from '../../../../../../hooks/useTextOverflow.js';
import { isZeroValueIgnoringDecimals } from '../../../../../../pages/Builder/BuilderHeader/BudgetField.jsx';
import {
  VersionsTableRow,
  VersionTableColumn,
} from '../../../../../../types/versions.js';
import {
  mapRowTypeToFontSize,
  mapRowTypeToFontWeight,
} from '../../../utils/index.js';

type Props = {
  value: string | number | null;
  column: VersionTableColumn;
  row: VersionsTableRow;
};

const VersionsBudgetField = ({ value, column, row }: Props) => {
  const scale = useTableScale();

  const { data: currency } = useCampaignDataSelector('currency');

  const { permissions } = usePermissions();
  const isNonFinancialUser = !permissions.financialDataFeature;
  const isCurrentVersion = !!row.original.isCurrentVersion;

  const textElementRef = useRef<HTMLElement | null>(null);

  const isOverflowed = useTextOverflow({ textElementRef });

  const renderFormattingField = () => {
    const decimals = column.decimals ?? 2;
    if (isNonFinancialUser && column.isCurrency) {
      return '---';
    }
    if (!value || (value && isZeroValueIgnoringDecimals(value))) {
      return '-';
    }
    if (value && column.isCurrency) {
      return (
        <ConditionalCurrencyField
          currency={currency}
          value={value}
          decimals={decimals}
        />
      );
    }

    if (
      value &&
      (column.id === 'vtr' || column.id === 'ctr' || column.id === 'cr')
    ) {
      return (
        <StaticPercentSpan
          number={value}
          options={{ minDecimals: decimals, maxDecimals: decimals }}
        />
      );
    }

    if (value) {
      return (
        <StaticNumericSpan
          number={value}
          options={{ minDecimals: decimals, maxDecimals: decimals }}
        />
      );
    }

    return value;
  };

  return (
    <Wrapper
      $scale={scale}
      $depth={row.depth}
      type={row.original.type as VERSION_ROW_TYPES}
    >
      <Tooltip
        placement="top"
        arrow
        title={renderFormattingField()}
        disableHoverListener={!isOverflowed}
      >
        <TextWrapper
          $width={Number(column.width)}
          $scale={scale}
          ref={textElementRef}
          type={row.original.type}
          isCurrentVersion={isCurrentVersion}
        >
          {renderFormattingField()}
        </TextWrapper>
      </Tooltip>
    </Wrapper>
  );
};

const WrapperCss = ({
  $scale,
  type,
}: {
  $depth: number;
  $scale: number;
  type: VERSION_ROW_TYPES;
}) => css`
  padding: 0 ${17 * $scale}px;
  font-weight: ${mapRowTypeToFontWeight(type)};
  font-size: ${$scale * mapRowTypeToFontSize(type)}rem;
`;

const Wrapper = styled('div')`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  cursor: default;
  white-space: nowrap;
  ${WrapperCss}
`;

const TextWrapper = styled('span')<{
  $width: number;
  $scale: number;
  type: string;
  isCurrentVersion: boolean;
}>`
  overflow: hidden;
  white-space: nowrap;
  color: ${({ isCurrentVersion, theme }) =>
    isCurrentVersion
      ? theme.palette.dataVisualization.emerald600
      : theme.palette.text.secondary};
  text-overflow: ellipsis;
  max-width: ${({ $width, $scale }) => css`
    ${$width * $scale}px
  `};
`;

export default VersionsBudgetField;
